import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate, useParams, Link} from 'react-router-dom';
import api from '../../api'; // For fetching plan details AND activation POST
import {useNotification} from '../../context/NotificationContext';
import Cookies from 'js-cookie'; // Needed for CSRF token on creation POST
import Loader from '../Loader';
import SignUpVideo from './SignUpVideo'; // Keep the video background
import ToggleBillingPeriodNew from '../../components/Form/Input/ToggleBillingPeriodNew'; // Use the new toggle
import SmsConsentCheckbox from '../Form/SmsConsentCheckbox';

// --- ORIGINAL STYLES ---
const styles = {
    pageContainer: {
        display: 'flex',
        height: '80vh',
        width: '100%',
        overflow: 'hidden',
    },
    videoWrapper: {
        flex: '0 0 50%',
        position: 'relative',
        height: '80vh',
        overflow: 'hidden',
    },
    contentContainer: {
        flex: 1,
        height: '80vh',
        backgroundColor: '#f4f7f9',
        padding: '40px',
        overflowY: 'auto',
        boxSizing: 'border-box',
    },
    infoBlock: {
        padding: '30px 40px',
        maxWidth: '700px',
        width: '100%',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
    },
    heading: {
        borderBottom: '1px solid #eee',
        paddingBottom: '15px',
        marginBottom: '25px',
        color: '#333',
        fontSize: '24px',
        fontWeight: '600',
        textAlign: 'center',
    },
    subHeading: {
        fontSize: '18px',
        color: '#555',
        marginBottom: '20px',
        textAlign: 'center',
    },
    infoSection: {
        marginBottom: '25px',
        paddingBottom: '20px',
        borderBottom: '1px solid #f0f0f0',
    },
    infoSectionTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#444',
        marginBottom: '15px',
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '12px',
        fontSize: '16px',
        flexWrap: 'wrap',
    },
    infoLabel: {
        fontWeight: 'bold',
        color: '#444',
        marginRight: '10px',
        marginBottom: '5px',
    },
    infoValue: {
        color: '#666',
        textAlign: 'right',
        wordBreak: 'break-word',
        marginLeft: 'auto',
    },
    addressValue: {
        color: '#666',
        textAlign: 'left',
        fontSize: '16px',
        wordBreak: 'break-word',
        marginTop: '5px',
        display: 'block',
        width: '100%',
    },
    specializationsList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        marginTop: '5px',
        textAlign: 'right',
        width: '100%',
    },
    specializationItem: {
        backgroundColor: '#e7f3ff',
        color: '#0a58ca',
        borderRadius: '4px',
        padding: '4px 8px',
        margin: '3px 0 3px 5px',
        display: 'inline-block',
        fontSize: '14px',
    },
    subscriptionSection: {
        marginTop: '20px',
        paddingTop: '20px',
        borderTop: '1px solid #eee',
    },
    trialNotification: {
        marginTop: '15px',
        padding: '12px',
        border: '1px solid #0d6efd',
        borderRadius: '5px',
        backgroundColor: '#cfe2ff',
        fontSize: '1.1em',
        color: '#0a58ca',
        lineHeight: '1.4',
        textAlign: 'center',
    },
    termsText: {
        fontSize: '14px',
        color: '#666',
        marginTop: '20px',
        textAlign: 'center',
        lineHeight: '1.5',
    },
    termsLink: {
        color: '#007bff',
        textDecoration: 'none',
    },
    errorBox: {
        padding: '20px',
        borderRadius: '5px',
        marginBottom: '20px',
        textAlign: 'center',
        backgroundColor: '#fff2f2', // Keep original style for load errors
        border: '1px solid #ffcccc',
        color: '#cc0000',
    },
    submissionErrorBox: { // Style for submission errors
        padding: '15px',
        borderRadius: '5px',
        marginBottom: '15px',
        textAlign: 'center',
        backgroundColor: '#f8d7da', // e.g., Bootstrap danger background
        border: '1px solid #f5c6cb', // e.g., Bootstrap danger border
        color: '#721c24', // e.g., Bootstrap danger text
    },
    buttonContainer: {
        marginTop: '30px',
        textAlign: 'center',
    },
    proceedButton: {
        padding: '12px 25px',
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#fff',
        backgroundColor: '#007bff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease',
    },
};

// --- Main Component ---
const DisplayPromoAttorneyInfo = () => {
    const {token} = useParams();
    const navigate = useNavigate();
    const {showNotification} = useNotification();

    // --- State variables ---
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // For initial data fetch errors
    const [submissionError, setSubmissionError] = useState(null);
    const [entityData, setEntityData] = useState(null);
    const [lawyerPlanLoading, setLawyerPlanLoading] = useState(true);
    const [lawyerPlanData, setLawyerPlanData] = useState(null);
    const [isProceeding, setIsProceeding] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const [smsNotificationsConsent, setSmsNotificationsConsent] = useState(false);
    const [smsMarketingConsent, setSmsMarketingConsent] = useState(false);

    // --- Helper: Get Request Config (MODIFIED FOR JSON) ---
    const getRequestConfig = useCallback(() => {
        const csrfToken = Cookies.get('csrftoken');
        const headers = {
            'Content-Type': 'application/json' // Set content type to JSON
        };
        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        } else {
            console.warn('CSRF token not found. POST requests may fail.');
            showNotification("Error", "A required security token is missing. Please refresh the page.", null, "error");
            return null;
        }
        return {
            headers: headers,
            withCredentials: true,
        };
    }, [showNotification]);

    // --- Fetch Entity and Plan Data (Original Logic - Unchanged) ---
    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);
        setSubmissionError(null);
        setLawyerPlanLoading(true);

        const fetchData = async () => {
            if (!token) {
                if (isMounted) {
                    setError("Token not found in URL.");
                    setLoading(false);
                    setLawyerPlanLoading(false);
                    showNotification('Error', 'Required token missing from URL.', null, 'error');
                }
                return;
            }

            let entityFetchError = null;
            // Fetch Entity Data
            try {
                // Use the correct endpoint to fetch entity details based on the token
                const response = await api.get(`/claim/info/${token}/`); // Assuming this endpoint returns Attorney or LegalCompany details
                if (response.status >= 200 && response.status < 300 && isMounted) {
                    setEntityData(response.data);
                    console.log("Fetched Entity Data:", response.data); // Log fetched data
                } else if (isMounted) {
                    entityFetchError = response.data?.detail || `Could not retrieve details (Status: ${response.status}).`;
                }
            } catch (err) {
                if (isMounted) {
                    let errorMsg = "Error fetching details.";
                    if (err.response) {
                        errorMsg = err.response.data?.detail || `Server error (${err.response.status}).`;
                        if (err.response.status === 404) errorMsg = "Invalid or expired token.";
                    } else {
                        errorMsg = "Network error.";
                    }
                    entityFetchError = errorMsg;
                }
            }

            let planFetchFailed = false;
            // Fetch Plan Data (Assuming 'pro' is the correct plan slug for attorneys/companies)
            try {
                const planResponse = await api.get('/plans/pro/'); // Adjust if plan depends on entity type
                if (planResponse.status >= 200 && planResponse.status < 300 && isMounted) {
                    setLawyerPlanData(planResponse.data);
                    console.log("Fetched Plan Data:", planResponse.data);
                } else if (isMounted) {
                    planFetchFailed = true;
                    setLawyerPlanData(null);
                    showNotification("Warning", "Could not load subscription plan details.", null, "warning");
                }
            } catch (err) {
                if (isMounted) {
                    planFetchFailed = true;
                    setLawyerPlanData(null);
                    showNotification("Error", "Failed to load subscription plan details.", null, "error");
                }
            } finally {
                if (isMounted) {
                    setLawyerPlanLoading(false);
                }
            }

            // Set final states
            if (isMounted) {
                if (entityFetchError) {
                    setError(entityFetchError);
                    showNotification('Error', entityFetchError, null, 'error');
                } else if (planFetchFailed && !entityData) {
                    setError("Failed to load required plan information.");
                    showNotification('Error', "Failed to load plan information.", null, 'error');
                }
                setLoading(false);
            }
        };
        fetchData();
        return () => {
            isMounted = false;
        };
    }, [token, showNotification]); // Removed navigate dependency as it wasn't used here

    // --- Determine if entity is Company or Attorney ---
    // NOTE: Adjust this logic based on the *actual* fields returned by your /claim/info/${token}/ endpoint
    const isCompanyData = entityData ? (!!entityData.attorneys || (!entityData.first_name && !!entityData.name)) : false;
    // You might need a more reliable field, e.g., entityData.entity_type === 'company'

    // --- Helper function formatAddress (Original - Unchanged) ---
    const formatAddress = useCallback((addr) => {
        if (!addr) return 'N/A';
        if (typeof addr === 'object' && addr !== null) {
            const parts = [addr.street_address, addr.city?.name, addr.state?.name, addr.zip_code?.zip_code].filter(Boolean);
            return parts.join(', ') || 'N/A';
        }
        if (typeof addr === 'string') return addr.trim() || 'N/A';
        return 'N/A';
    }, []);

    // --- Helper function formatPracticeAreas (Original - Unchanged) ---
    const formatPracticeAreas = useCallback((areasOrSpecs) => {
        let areasToShow = [];
        if (!areasOrSpecs || !Array.isArray(areasOrSpecs)) return <span style={styles.infoValue}>N/A</span>;
        if (areasOrSpecs.every(item => typeof item === 'string')) areasToShow = areasOrSpecs.filter(Boolean);
        else if (areasOrSpecs.every(item => typeof item === 'object' && item?.name)) areasToShow = areasOrSpecs.map(spec => spec.name).filter(Boolean);
        if (areasToShow.length === 0) return <span style={styles.infoValue}>N/A</span>;
        return <ul style={styles.specializationsList} aria-label="Practice Areas">{areasToShow.map((area, index) => <li
            key={index} style={styles.specializationItem}>{area}</li>)}</ul>;
    }, []);


    // --- Handle Proceed Button Click (MODIFIED TO SEND ENTITY ID) ---
    const handleProceed = useCallback(async () => {
        // Basic checks
        if (!entityData || !lawyerPlanData || !token) {
            showNotification("Error", "Cannot proceed. Missing required data or plan information.", null, "error");
            return;
        }
        if (lawyerPlanLoading) {
            showNotification("Info", "Please wait, plan details are loading.", null, "info");
            return;
        }
        setIsProceeding(true);
        setSubmissionError(null); // Clear previous submission error

        // Get CSRF config
        const config = getRequestConfig();
        if (!config) {
            setIsProceeding(false);
            return; // Error already shown by getRequestConfig
        }

        // --- CONSTRUCT PAYLOAD WITH ENTITY ID ---
        const payload = {
            promo_token: token,
            billing_period: billingPeriod,
            sms_notifications_consent: smsNotificationsConsent,
            sms_marketing_consent: smsMarketingConsent,
        };

        // Determine entity ID and add it to the payload
        let entityId = null;
        let idFieldName = null;

        if (isCompanyData) {
            // For LegalCompany, Django's default PK is 'id'
            entityId = entityData.id;
            idFieldName = 'company_id'; // Backend expects 'company_id'
            console.log(`Entity identified as Company. Using ID: ${entityId} as company_id.`);
        } else {
            // For Attorney, the model defines 'attorney_id' as PK. Check if it's in the response.
            if (entityData.attorney_id) {
                entityId = entityData.attorney_id;
                idFieldName = 'attorney_id'; // Use 'attorney_id' if backend supports it
                console.log(`Entity identified as Attorney. Using ID: ${entityId} as attorney_id.`);
            } else if (entityData.id) {
                // Fallback if 'attorney_id' is not present but 'id' is (less likely based on model)
                entityId = entityData.id;
                idFieldName = 'attorney_id'; // Still label it as attorney_id for backend
                console.warn(`Entity identified as Attorney but 'attorney_id' missing. Using fallback 'id': ${entityId} as attorney_id.`);
            } else {
                console.error("Could not determine Attorney ID from entityData:", entityData);
            }
        }

        // Add the ID to the payload if found
        if (entityId && idFieldName) {
            payload[idFieldName] = entityId;
        } else {
            // Handle missing ID critical error
            const entityType = isCompanyData ? "Company" : "Attorney";
            console.error(`Critical Error: Could not find ID for ${entityType}. Payload incomplete.`);
            showNotification("Error", `Cannot proceed. The ID for the ${entityType} is missing.`, null, "error");
            setSubmissionError(`The ID for the ${entityType} could not be determined.`);
            setIsProceeding(false);
            return; // Stop execution
        }
        // --- END PAYLOAD CONSTRUCTION ---


        // API Call to the checkout session endpoint
        try {
            console.log("Requesting checkout session with payload:", JSON.stringify(payload, null, 2)); // Log final payload
            const response = await api.post('/promo/create-checkout-session/', payload, config);
            const responseData = response.data;

            // Success: Check status 200 and checkout_url presence
            if (response.status === 200 && responseData?.checkout_url) {
                const checkoutUrl = responseData.checkout_url;
                showNotification("Success", "Activation initiated! Redirecting to secure payment...", null, "success");
                console.log(`REDIRECTING to Stripe: ${checkoutUrl}`);
                window.location.href = checkoutUrl; // Redirect user to Stripe
                // No need to setIsProceeding(false) here as we are navigating away
                return;
            } else {
                // Handle unexpected success response or missing URL
                const detail = responseData?.detail || `Failed to get checkout session (Status: ${response.status}). Check server logs.`;
                console.error("API Error (Success Status, Invalid Data):", response.status, responseData);
                setSubmissionError(detail);
                showNotification('Error', detail, null, 'error');
                setIsProceeding(false);
            }
        } catch (err) {
            // Handle API errors (4xx, 5xx, network)
            console.error("Error requesting checkout session:", err.response || err.message);
            let errorMsg = "An unexpected error occurred while initiating activation.";
            if (err.response) {
                const errorData = err.response.data;
                // Try to get specific error messages
                if (errorData && typeof errorData === 'object') {
                    errorMsg = errorData.detail ||
                        errorData.promo_token?.[0] ||
                        errorData.company_id?.[0] ||   // Check for company_id errors
                        errorData.attorney_id?.[0] ||  // Check for attorney_id errors
                        errorData.billing_period?.[0] ||
                        `Server error (${err.response.status}).`;
                } else if (typeof errorData === 'string') {
                    errorMsg = errorData;
                } else {
                    errorMsg = `Server error (${err.response.status}).`;
                }

                // Specific status code handling
                if (err.response.status === 400 && errorMsg.includes("ID")) {
                    // More specific message if ID was the problem
                    errorMsg = `There was an issue verifying the entity ID (${idFieldName}). Please contact support.`;
                } else if (err.response.status === 404) {
                    errorMsg = errorData?.detail || "Invalid or expired activation link, or entity not found.";
                } else if (err.response.status === 403 && errorMsg.toLowerCase().includes('csrf')) {
                    errorMsg = "Security check failed. Please refresh the page and try again.";
                }

            } else {
                errorMsg = "Network error. Please check your connection.";
            }

            setSubmissionError(errorMsg);
            showNotification('Error', errorMsg, null, 'error');
            setIsProceeding(false); // Allow retry
        }
    }, [
        token,
        billingPeriod,
        entityData, // Depends on entityData for ID and type check
        isCompanyData, // Depends on the derived isCompanyData flag
        lawyerPlanData,
        lawyerPlanLoading,
        smsNotificationsConsent,
        smsMarketingConsent,
        getRequestConfig,
        showNotification,
        // navigate, // Only include if used elsewhere in this function
    ]); // Updated dependencies


    // --- Render Logic (Minor adjustments for clarity, mostly original) ---

    if (loading) {
        return <Loader/>; // Original Loader usage
    }

    // Derived display values (using isCompanyData where appropriate)
    const lawyerMonthlyPrice = lawyerPlanData?.monthly_price;
    const lawyerAnnualPrice = lawyerPlanData?.annual_price;
    const lawyerEffectiveMonthlyPrice = lawyerAnnualPrice ? (parseFloat(lawyerAnnualPrice) / 12).toFixed(0) : null;
    const canProceed = !!entityData && !!lawyerPlanData && !isProceeding && !loading && !lawyerPlanLoading;

    const displayName = entityData?.name || `${entityData?.first_name || ''} ${entityData?.last_name || ''}`.trim() || 'N/A';
    const displayEmail = entityData?.email || 'N/A';
    const displayPhone = entityData?.phone_number || entityData?.phone || 'N/A';
    // Firm name display logic slightly adjusted for clarity
    const displayFirmName = isCompanyData ? (entityData?.name || 'N/A') : (entityData?.legal_company?.name || entityData?.firm_name || 'N/A'); // Adjust based on actual API response field for attorney's firm
    const displayAddress = formatAddress(entityData?.address);
    const displayPracticeAreas = formatPracticeAreas(entityData?.specializations || entityData?.practice_areas); // Assumes field name consistency
    // Attorney specific fields
    const displayLicenseYear = !isCompanyData ? (entityData?.admission_date ? entityData.admission_date.split('-')[0] : (entityData?.license_year || 'N/A')) : 'N/A';
    const displayBarNumber = !isCompanyData ? (entityData?.bar_code ? String(entityData.bar_code) : (entityData?.registration_number || 'N/A')) : 'N/A';
    // Company specific field
    const displayFoundationYear = isCompanyData ? (entityData?.foundation_year || 'N/A') : 'N/A';

    const pageTitle = isCompanyData ? "Company Promotion Details" : "Attorney Promotion Details"; // Adjusted title
    const buttonText = isProceeding ? 'Processing...' : (isCompanyData ? 'Start Trial: $1 for 90 Days' : 'Start Trial: $1 for 90 Days'); // Adjusted button text

    // --- Original JSX Structure (with minor title/text adjustments) ---
    return (
        <div style={styles.pageContainer}>
            <div style={styles.videoWrapper}>
                <SignUpVideo style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
            </div>
            <div style={styles.contentContainer}>
                <div style={styles.infoBlock}>

                    <h1 style={styles.heading}>{pageTitle}</h1>

                    {/* Initial Load Error Display */}
                    {error && !entityData && (
                        <div style={styles.errorBox}>
                            <h2 style={{marginTop: 0, color: '#cc0000', fontSize: '18px'}}>Unable to Retrieve
                                Information</h2>
                            <p><strong>{error}</strong></p>
                            <p>This activation link may be invalid or expired. Please check the link or <Link
                                to="/sign-up" style={styles.termsLink}>sign up manually</Link>.</p>
                        </div>
                    )}

                    {/* Submission Error Display */}
                    {submissionError && (
                        <div style={styles.submissionErrorBox}>
                            <p style={{margin: 0}}><strong>Activation Error:</strong> {submissionError}</p>
                        </div>
                    )}

                    {/* Main Content */}
                    {entityData ? (
                        <>
                            <p style={styles.subHeading}>
                                Please review your details below and choose your preferred billing cycle for the Pro
                                plan trial.
                            </p>

                            {/* Contact Info Section */}
                            <div style={styles.infoSection}>
                                <h2 style={styles.infoSectionTitle}>Contact Information</h2>
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>{isCompanyData ? 'Company Name:' : 'Name:'}</span>
                                    <span style={styles.infoValue}>{displayName}</span>
                                </div>
                                <div style={styles.infoRow}><span style={styles.infoLabel}>Email:</span><span
                                    style={styles.infoValue}>{displayEmail}</span></div>
                                <div style={styles.infoRow}><span style={styles.infoLabel}>Phone:</span><span
                                    style={styles.infoValue}>{displayPhone}</span></div>
                                {/* Attorney Specific */}
                                {displayLicenseYear !== 'N/A' && !isCompanyData &&
                                    <div style={styles.infoRow}><span style={styles.infoLabel}>License Year:</span><span
                                        style={styles.infoValue}>{displayLicenseYear}</span></div>}
                                {displayBarNumber !== 'N/A' && !isCompanyData &&
                                    <div style={styles.infoRow}><span style={styles.infoLabel}>Bar Number:</span><span
                                        style={styles.infoValue}>{displayBarNumber}</span></div>}
                            </div>

                            {/* Firm/Location Info Section */}
                            <div style={styles.infoSection}>
                                <h2 style={styles.infoSectionTitle}>{isCompanyData ? 'Company Details' : 'Firm / Location Information'}</h2>
                                {/* Show Firm Name for Attorney if different from Attorney Name */}
                                {!isCompanyData && displayFirmName !== 'N/A' && displayName !== displayFirmName && (
                                    <div style={styles.infoRow}><span style={styles.infoLabel}>Firm Name:</span><span
                                        style={styles.infoValue}>{displayFirmName}</span></div>
                                )}
                                {/* Company Specific */}
                                {displayFoundationYear !== 'N/A' && isCompanyData &&
                                    <div style={styles.infoRow}><span style={styles.infoLabel}>Founded:</span><span
                                        style={styles.infoValue}>{displayFoundationYear}</span></div>}
                                <div style={styles.infoRow}><span style={styles.infoLabel}>Address:</span></div>
                                <span style={styles.addressValue}>{displayAddress}</span>
                                <div style={{...styles.infoRow, marginTop: '15px', alignItems: 'flex-start'}}>
                                    <span style={styles.infoLabel}>Practice Areas:</span>
                                    {displayPracticeAreas}
                                </div>
                            </div>

                            {/* Subscription Section */}
                            <div style={styles.subscriptionSection}>
                                <h2 style={styles.infoSectionTitle}>Pro Plan Trial Activation</h2>
                                {lawyerPlanLoading ? (
                                    <div style={{textAlign: 'center', color: '#888', padding: '10px 0'}}><Loader
                                        size="small" message="Loading plan details..."/></div>
                                ) : lawyerPlanData ? (
                                    <>
                                        <ToggleBillingPeriodNew
                                            billingPeriod={billingPeriod}
                                            onBillingPeriodChange={setBillingPeriod}
                                            disabled={isProceeding || !canProceed}
                                            monthlyPrice={lawyerMonthlyPrice}
                                            annualPrice={lawyerAnnualPrice}
                                            effectiveMonthlyPrice={lawyerEffectiveMonthlyPrice}
                                        />
                                        <div style={styles.trialNotification}>
                                            <strong>Important:</strong> You will be charged <strong>$1.00
                                            today</strong> for a <strong>90-day trial</strong> of the Pro Plan. After
                                            the trial, your selected plan
                                            ({billingPeriod === 'monthly' ? `$${lawyerMonthlyPrice}/month` : `$${lawyerAnnualPrice}/year, billed annually`})
                                            will begin automatically unless cancelled. You can manage your subscription
                                            anytime in your dashboard after activation.
                                        </div>
                                    </>
                                ) : (
                                    <div style={{
                                        ...styles.errorBox,
                                        backgroundColor: '#fffbe6',
                                        borderColor: '#ffe58f',
                                        color: '#664d03',
                                        marginBottom: '15px'
                                    }}>
                                        Subscription plan details could not be loaded. Activation cannot proceed without
                                        plan information. Please try refreshing or contact support.
                                    </div>
                                )}
                            </div>

                            {/* Terms and Conditions Text */}
                            <div style={styles.termsText}>
                                By clicking "{buttonText}", you agree to charge $1.00 for the trial and to our <br/>
                                <Link to="/terms-of-use" target="_blank" rel="noopener noreferrer"
                                      style={styles.termsLink}>Terms of Use</Link> and {' '}
                                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer"
                                      style={styles.termsLink}>Privacy Policy</Link>.
                            </div>

                            <SmsConsentCheckbox
                                checked={smsNotificationsConsent}
                                onChange={(e) => setSmsNotificationsConsent(e.target.checked)}
                                style={{marginTop: '15px'}}
                            />
                            <SmsConsentCheckbox
                                name="smsMarketingConsent"
                                checked={smsMarketingConsent}
                                onChange={(e) => setSmsMarketingConsent(e.target.checked)}
                                style={{marginTop: '15px'}}
                                variant="marketing"
                            />

                            {/* Action Button Container */}
                            <div style={styles.buttonContainer}>
                                <button
                                    style={{
                                        ...styles.proceedButton, ...(!canProceed ? {
                                            backgroundColor: '#cccccc',
                                            cursor: 'not-allowed',
                                            opacity: 0.7
                                        } : {})
                                    }}
                                    onClick={handleProceed}
                                    disabled={!canProceed || isProceeding} // Disable also when proceeding
                                    aria-disabled={!canProceed || isProceeding}
                                >
                                    {buttonText}
                                </button>
                                {!lawyerPlanData && !lawyerPlanLoading && entityData && (
                                    <p style={{color: '#dc3545', marginTop: '10px', fontSize: '14px'}}>
                                        Activation is currently disabled because required plan details could not be
                                        loaded. Please try again later.
                                    </p>
                                )}
                                <p style={{...styles.termsText, fontSize: '12px', marginTop: '15px'}}>
                                    Request profile removal: email <a href="mailto:law@law.law"
                                                                      style={styles.termsLink}>law@law.law</a>.
                                </p>
                            </div>
                        </>
                    ) : (
                        // Fallback when no entityData but no load error (e.g., token valid but no data somehow)
                        !loading && !error && (
                            <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                No specific information is available for this activation link. It might be invalid or
                                expired.
                            </div>
                        )
                    )}
                </div>
                {/* End infoBlock */}
            </div>
            {/* End contentContainer */}
        </div> // End pageContainer
    );
};

export default DisplayPromoAttorneyInfo;
